<script>import {
  config,
  formDataToObject,
  FormComponentResetSymbol,
  resetFormFields,
  mergeDataIntoQueryString,
  isUrlMethodPair,
  UseFormUtils
} from "@inertiajs/core";
import {} from "laravel-precognition";
import { isEqual } from "lodash-es";
import { onMount, setContext } from "svelte";
import { writable } from "svelte/store";
import { FormContextKey } from "./formContext";
import useForm from "../useForm";
const noop = () => void 0;
export let action = "";
export let method = "get";
export let headers = {};
export let queryStringArrayFormat = "brackets";
export let errorBag = null;
export let showProgress = true;
export let transform = (data) => data;
export let options = {};
export let onCancelToken = noop;
export let onBefore = noop;
export let onStart = noop;
export let onProgress = noop;
export let onFinish = noop;
export let onCancel = noop;
export let onSuccess = noop;
export let onError = noop;
export let onSubmitComplete = noop;
export let disableWhileProcessing = false;
export let invalidateCacheTags = [];
export let resetOnError = false;
export let resetOnSuccess = false;
export let setDefaultsOnSuccess = false;
export let validateFiles = false;
export let validationTimeout = 1500;
export let withAllErrors = null;
const getTransformedData = () => {
  const [_url, data] = getUrlAndData();
  return transform(data);
};
const form = useForm({}).withPrecognition(
  () => _method,
  () => getUrlAndData()[0]
).setValidationTimeout(validationTimeout);
if (validateFiles) {
  form.validateFiles();
}
if (withAllErrors ?? config.get("form.withAllErrors")) {
  form.withAllErrors();
}
form.transform(getTransformedData);
let formElement;
let isDirty = false;
let defaultData = new FormData();
$: _method = isUrlMethodPair(action) ? action.method : (method ?? "get").toLowerCase();
$: _action = isUrlMethodPair(action) ? action.url : action;
export function getFormData(submitter) {
  return new FormData(formElement, submitter);
}
export function getData(submitter) {
  return formDataToObject(getFormData(submitter));
}
function getUrlAndData(submitter) {
  return mergeDataIntoQueryString(_method, _action, getData(submitter), queryStringArrayFormat);
}
function updateDirtyState(event) {
  if (event.type === "reset" && event.detail?.[FormComponentResetSymbol]) {
    event.preventDefault();
  }
  isDirty = event.type === "reset" ? false : !isEqual(getData(), formDataToObject(defaultData));
}
export function submit(submitter) {
  const [url, data] = getUrlAndData(submitter);
  const formTarget = submitter?.getAttribute("formtarget");
  if (formTarget === "_blank" && _method === "get") {
    window.open(url, "_blank");
    return;
  }
  const maybeReset = (resetOption) => {
    if (!resetOption) {
      return;
    }
    if (resetOption === true) {
      reset();
    } else if (resetOption.length > 0) {
      reset(...resetOption);
    }
  };
  const submitOptions = {
    headers,
    queryStringArrayFormat,
    errorBag,
    showProgress,
    invalidateCacheTags,
    onCancelToken,
    onBefore,
    onStart,
    onProgress,
    onFinish,
    onCancel,
    onSuccess: (...args) => {
      if (onSuccess) {
        onSuccess(...args);
      }
      if (onSubmitComplete) {
        onSubmitComplete({
          reset,
          defaults
        });
      }
      maybeReset(resetOnSuccess);
      if (setDefaultsOnSuccess === true) {
        defaults();
      }
    },
    onError: (...args) => {
      if (onError) {
        onError(...args);
      }
      maybeReset(resetOnError);
    },
    ...options
  };
  $form.transform(() => transform(data)).submit(_method, url, submitOptions);
  $form.transform(getTransformedData);
}
function handleSubmit(event) {
  event.preventDefault();
  submit(event.submitter);
}
function handleReset(event) {
  if (event.isTrusted) {
    event.preventDefault();
    reset();
  }
}
export function reset(...fields) {
  resetFormFields(formElement, defaultData, fields);
  form.reset(...fields);
}
export function clearErrors(...fields) {
  $form.clearErrors(...fields);
}
export function resetAndClearErrors(...fields) {
  clearErrors(...fields);
  reset(...fields);
}
export function setError(fieldOrFields, maybeValue) {
  $form.setError(typeof fieldOrFields === "string" ? { [fieldOrFields]: maybeValue } : fieldOrFields);
}
export function defaults() {
  defaultData = getFormData();
  isDirty = false;
}
export function validate(field, config2) {
  return form.validate(...UseFormUtils.mergeHeadersForValidation(field, config2, headers));
}
export function valid(field) {
  return form.valid(field);
}
export function invalid(field) {
  return form.invalid(field);
}
export function touch(field, ...fields) {
  return form.touch(field, ...fields);
}
export function touched(field) {
  return form.touched(field);
}
export function validator() {
  return form.validator();
}
onMount(() => {
  defaultData = getFormData();
  form.defaults(getData());
  const formEvents = ["input", "change", "reset"];
  formEvents.forEach((e) => formElement.addEventListener(e, updateDirtyState));
  return () => {
    formEvents.forEach((e) => formElement?.removeEventListener(e, updateDirtyState));
  };
});
$: {
  form.setValidationTimeout(validationTimeout);
  if (validateFiles) {
    form.validateFiles();
  } else {
    form.withoutFileValidation();
  }
}
$: slotErrors = $form.errors;
const formContextStore = writable(void 0);
$: formContextStore.set({
  errors: $form.errors,
  hasErrors: $form.hasErrors,
  processing: $form.processing,
  progress: $form.progress,
  wasSuccessful: $form.wasSuccessful,
  recentlySuccessful: $form.recentlySuccessful,
  isDirty,
  clearErrors,
  resetAndClearErrors,
  setError,
  reset,
  submit,
  defaults,
  getData,
  getFormData,
  // Precognition
  validator,
  validate,
  touch,
  validating: $form.validating,
  valid,
  invalid,
  touched
});
setContext(FormContextKey, formContextStore);
</script>

<form
  bind:this={formElement}
  action={_action}
  method={_method}
  on:submit={handleSubmit}
  on:reset={handleReset}
  {...$$restProps}
  inert={disableWhileProcessing && $form.processing ? true : undefined}
>
  <slot
    errors={slotErrors}
    hasErrors={$form.hasErrors}
    processing={$form.processing}
    progress={$form.progress}
    wasSuccessful={$form.wasSuccessful}
    recentlySuccessful={$form.recentlySuccessful}
    {clearErrors}
    {resetAndClearErrors}
    {setError}
    {isDirty}
    {submit}
    {defaults}
    {reset}
    {getData}
    {getFormData}
    {validator}
    {validate}
    {touch}
    validating={$form.validating}
    valid={$form.valid}
    invalid={$form.invalid}
    touched={$form.touched}
  />
</form>
